import React, { useState } from "react"
import { Tooltip } from "antd"
import {
	ClockCircleOutlined,
	HomeOutlined,
	ThunderboltOutlined,
} from "@ant-design/icons"
import Strings from "utils/strings"
import { Route, RouteAddress } from "../../types"
import { ReadOnlyStop } from "./types"
import {
	MOVED_COLOR,
	REPOSITIONED_COLOR,
	TOOLTIP_COLOR,
	formatClock,
} from "./utils"
import ReadOnlyPill from "./ReadOnlyPill"

const LABEL_COL_WIDTH = 108
const ROW_HEIGHT = 40
const GAP_HEIGHT = 26
const TOTAL_HEIGHT = ROW_HEIGHT * 2 + GAP_HEIGHT
// Keeps the first/last stop off the card's edge — it clips (overflow:hidden).
const EDGE_INSET_PERCENT = 4

const DepotTag: React.FC<{
	addr: RouteAddress
	left: number
	color: string
	muted?: boolean
}> = ({ addr, left, color, muted = false }) => (
	<Tooltip
		color={TOOLTIP_COLOR}
		title={
			<div style={{ minWidth: 160 }}>
				<div style={{ fontWeight: 600, marginBottom: 4 }}>
					{Strings.routes.timelineLegendDepot}
				</div>
				{addr.address && <div style={{ color: "#bfbfbf" }}>{addr.address}</div>}
				<div style={{ marginTop: 4 }}>{formatClock(addr.eta)}</div>
			</div>
		}
	>
		<div
			style={{
				position: "absolute",
				left: `${left}%`,
				top: "50%",
				transform: "translate(-50%, -50%)",
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				width: 26,
				height: 26,
				boxSizing: "border-box",
				borderRadius: 7,
				background: muted ? "#eef0f3" : color,
				border: muted ? "1px solid #dde1e6" : "none",
			}}
		>
			<HomeOutlined
				style={{ color: muted ? "#6b7280" : "#fff", fontSize: 12 }}
			/>
		</div>
	</Tooltip>
)

// Static counterpart to RouteRow.tsx — positioned by sequence, not real ETA.
// Current/proposed rows are linked by a faint connector per stop that lights
// up on hover, so a busy route stays traceable one stop at a time.
const ReadOnlyRouteRow: React.FC<{
	route: Route
	color: string
	currentStops: { addr: RouteAddress; sequence: number }[]
	optimizedStops: ReadOnlyStop[]
}> = ({ route, color, currentStops, optimizedStops }) => {
	const [hoveredId, setHoveredId] = useState<string | null>(null)

	// Depots before the first delivery are the outbound leg, after it the return leg.
	const allAddresses = route.addresses || []
	const firstDeliveryIndex = allAddresses.findIndex(
		(a) => !a.isDepot && a.order?.orderId
	)
	const isBeforeDeliveries = (index: number) =>
		firstDeliveryIndex === -1 || index < firstDeliveryIndex
	const startDepots = allAddresses.filter(
		(a, i) => a.isDepot && isBeforeDeliveries(i)
	)
	const endDepots = allAddresses.filter(
		(a, i) => a.isDepot && !isBeforeDeliveries(i)
	)

	const inset = (percent: number) =>
		EDGE_INSET_PERCENT + (percent / 100) * (100 - EDGE_INSET_PERCENT * 2)
	const positionForIndex = (index: number, total: number) =>
		inset(total > 1 ? (index / (total - 1)) * 100 : 50)

	// Vertical S-curve, not a straight diagonal — a shallow-angle line grazing
	// past an uninvolved stop reads as if it terminates there.
	const midY = ROW_HEIGHT / 2 + (ROW_HEIGHT + GAP_HEIGHT) / 2
	const pathFor = (x1: number, x2: number) =>
		`M ${x1} ${ROW_HEIGHT / 2} C ${x1} ${midY} ${x2} ${midY} ${x2} ${ROW_HEIGHT + GAP_HEIGHT + ROW_HEIGHT / 2}`

	const connectors = optimizedStops
		.map((s, i) => {
			if (s.previousSequence == null) return null
			const changed = s.changeType != null && s.changeType !== "none"
			const x1 = positionForIndex(s.previousSequence - 1, currentStops.length)
			const x2 = positionForIndex(i, optimizedStops.length)
			return {
				key: s.addr.order.orderId,
				path: pathFor(x1, x2),
				changed,
				stroke: !changed
					? "#dde1e6"
					: s.changeType === "moved"
						? MOVED_COLOR
						: REPOSITIONED_COLOR,
			}
		})
		.filter((c): c is NonNullable<typeof c> => c != null)

	return (
		<div style={{ padding: "14px 16px 18px" }}>
			<div style={{ display: "flex" }}>
				<div style={{ width: LABEL_COL_WIDTH, flexShrink: 0 }}>
					<div
						style={{
							height: ROW_HEIGHT,
							display: "flex",
							alignItems: "center",
							gap: 5,
						}}
					>
						<ClockCircleOutlined style={{ fontSize: 11, color: "#9aa0a8" }} />
						<span
							style={{
								fontSize: 11,
								fontWeight: 700,
								letterSpacing: "0.06em",
								color: "#9aa0a8",
								whiteSpace: "nowrap",
							}}
						>
							{Strings.routes.moveOrdersOptimizeCurrentLabel.toUpperCase()}
						</span>
					</div>
					<div style={{ height: GAP_HEIGHT }} />
					<div
						style={{
							height: ROW_HEIGHT,
							display: "flex",
							alignItems: "center",
							gap: 5,
						}}
					>
						<ThunderboltOutlined style={{ fontSize: 11, color }} />
						<span
							style={{
								fontSize: 11,
								fontWeight: 750,
								letterSpacing: "0.06em",
								color,
								whiteSpace: "nowrap",
							}}
						>
							{Strings.routes.moveOrdersOptimizeProposedLabel.toUpperCase()}
						</span>
					</div>
				</div>

				<div style={{ flex: 1, position: "relative" }}>
					<svg
						viewBox={`0 0 100 ${TOTAL_HEIGHT}`}
						preserveAspectRatio="none"
						style={{
							position: "absolute",
							inset: 0,
							width: "100%",
							height: TOTAL_HEIGHT,
							pointerEvents: "none",
						}}
					>
						{connectors.map((c) => {
							const isHovered = c.key === hoveredId
							const dimmed = hoveredId != null && !isHovered
							return (
								<path
									key={c.key}
									d={c.path}
									fill="none"
									stroke={c.stroke}
									strokeWidth={isHovered ? 2.5 : c.changed ? 1.5 : 1}
									opacity={
										dimmed ? 0.08 : isHovered ? 1 : c.changed ? 0.4 : 0.25
									}
									vectorEffect="non-scaling-stroke"
									style={{ transition: "opacity 0.1s ease" }}
								/>
							)
						})}
					</svg>

					<div style={{ position: "relative", height: ROW_HEIGHT }}>
						{startDepots.map((addr, i) => (
							<DepotTag
								key={`start-${i}`}
								addr={addr}
								left={inset(0)}
								color={color}
								muted
							/>
						))}
						{currentStops.map((s, i) => (
							<ReadOnlyPill
								key={s.addr.order.orderId}
								addr={s.addr}
								left={positionForIndex(i, currentStops.length)}
								color={color}
								sequence={s.sequence}
								muted
								onHover={(hovering) =>
									setHoveredId(hovering ? s.addr.order.orderId : null)
								}
							/>
						))}
						{endDepots.map((addr, i) => (
							<DepotTag
								key={`end-${i}`}
								addr={addr}
								left={inset(100)}
								color={color}
								muted
							/>
						))}
					</div>

					<div style={{ height: GAP_HEIGHT }} />

					<div style={{ position: "relative", height: ROW_HEIGHT }}>
						{startDepots.map((addr, i) => (
							<DepotTag
								key={`start-${i}`}
								addr={addr}
								left={inset(0)}
								color={color}
							/>
						))}
						{optimizedStops.map((s, i) => (
							<ReadOnlyPill
								key={s.addr.order.orderId}
								addr={s.addr}
								left={positionForIndex(i, optimizedStops.length)}
								eta={s.eta}
								color={color}
								sequence={s.sequence}
								changeType={s.changeType}
								previousSequence={s.previousSequence}
								previousRouteName={s.previousRouteName}
								pickupEta={s.pickupEta}
								onHover={(hovering) =>
									setHoveredId(hovering ? s.addr.order.orderId : null)
								}
							/>
						))}
						{endDepots.map((addr, i) => (
							<DepotTag
								key={`end-${i}`}
								addr={addr}
								left={inset(100)}
								color={color}
							/>
						))}
					</div>
				</div>
			</div>
		</div>
	)
}

export default ReadOnlyRouteRow
